import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Pencil, Ruler, RectangleHorizontal, Eraser, Undo2, Trash2, X as XIcon } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { uploadFileToBlob } from '../../api/services/storageService';
import { ConfirmDialog } from '../../components';

// ==================== Styled Components ====================

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const ModalCard = styled.div`
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  width: 92vw;
  max-width: 920px;
  max-height: 92vh;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  flex-shrink: 0;
`;

const ModalTitle = styled.span`
  font-weight: 600;
  font-size: 17px;
  color: ${props => props.theme.colors.text};
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: ${props => props.theme.colors.muted};
  line-height: 1;
  padding: 2px 6px;

  &:hover {
    color: ${props => props.theme.colors.text};
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  padding: ${props => props.theme.spacing.md};
  gap: ${props => props.theme.spacing.sm};
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.xs};
  flex-shrink: 0;
`;

const ToolBtn = styled.button`
  padding: 5px 11px;
  border: 2px solid ${props => props.$active ? props.theme.colors.tint : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.$active ? (props.theme.colors.tint + '22') : props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  font-size: 13px;
  font-weight: ${props => props.$active ? 600 : 400};
  transition: border-color 0.15s, background 0.15s;

  &:hover:not(:disabled) {
    border-color: ${props => props.theme.colors.tint};
    background: ${props => props.theme.colors.tint + '15'};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const DividerBar = styled.div`
  width: 1px;
  height: 22px;
  background: ${props => props.theme.colors.border};
  margin: 0 2px;
  flex-shrink: 0;
`;

const ColorSwatch = styled.button`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${props => props.$color};
  border: 3px solid ${props =>
    props.$active
      ? props.theme.colors.tint
      : (props.$color === '#ffffff' ? props.theme.colors.border : 'transparent')
  };
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition: transform 0.1s;

  &:hover {
    transform: scale(1.2);
  }
`;

const CanvasWrapper = styled.div`
  flex: 1;
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
  background: #ffffff;
  position: relative;
  min-height: 350px;
  cursor: crosshair;
`;

const StyledCanvas = styled.canvas`
  display: block;
  width: 100%;
  height: 100%;
  touch-action: none;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border-top: 1px solid ${props => props.theme.colors.border};
  flex-shrink: 0;
`;

const StatusMsg = styled.span`
  font-size: 12px;
  color: ${props => props.theme.colors.muted};
  margin-right: auto;
`;

const FooterBtn = styled.button`
  padding: 8px 20px;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.$primary ? 'transparent' : props.theme.colors.border};
  background: ${props => props.$primary ? props.theme.colors.tint : props.theme.colors.background};
  color: ${props => props.$primary ? '#ffffff' : props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ==================== Constants ====================

const COLORS = [
  '#000000', '#374151', '#dc2626', '#ea580c',
  '#ca8a04', '#16a34a', '#2563eb', '#7c3aed', '#ffffff',
];

const STROKE_WIDTHS = { thin: 2, medium: 5, thick: 12 };

const TOOLS = [
  { id: 'pen', label: 'Pen', icon: Pencil },
  { id: 'line', label: 'Line', icon: Ruler },
  { id: 'rect', label: 'Rect', icon: RectangleHorizontal },
  { id: 'eraser', label: 'Eraser', icon: Eraser },
];

const MAX_UNDO = 20;

// ==================== Component ====================

/**
 * DrawingCanvas — HTML5 canvas drawing modal.
 *
 * @param {Function} onSave - Called with blobPath string after successful upload
 * @param {Function} onClose - Called when modal is dismissed
 * @param {string|null} existingBlobPath - Blob path of an existing drawing to load
 * @param {number} userId - Current user ID (for temp blob path)
 */
const DrawingCanvas = ({ onSave, onClose, existingBlobPath = null, userId }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);

  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState('medium');
  const [isDrawing, setIsDrawing] = useState(false);
  const [undoStack, setUndoStack] = useState([]);
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);

  // Mutable refs for drawing state (not re-render triggers)
  const startPos = useRef(null);
  const strokeSnapshot = useRef(null);
  const isDrawingRef = useRef(false);

  // Keep isDrawing ref in sync
  useEffect(() => {
    isDrawingRef.current = isDrawing;
  }, [isDrawing]);

  // ---- Canvas init ----

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;

    canvas.width = wrapper.offsetWidth || 800;
    canvas.height = wrapper.offsetHeight || 500;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    setUndoStack([ctx.getImageData(0, 0, canvas.width, canvas.height)]);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      initCanvas();
      if (existingBlobPath) {
        loadExistingDrawing();
      }
    }, 80);
    return () => clearTimeout(timer);
  }, []);

  const loadExistingDrawing = async () => {
    try {
      setStatusMsg('Loading existing drawing…');
      const url = await getFileUrl(existingBlobPath);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setUndoStack([ctx.getImageData(0, 0, canvas.width, canvas.height)]);
        setStatusMsg('');
      };
      img.onerror = () => setStatusMsg('');
      img.src = url;
    } catch {
      setStatusMsg('');
    }
  };

  // ---- Drawing helpers ----

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * scaleX,
      y: (src.clientY - rect.top) * scaleY,
    };
  };

  const applyCtxStyle = (ctx) => {
    ctx.strokeStyle = tool === 'eraser' ? '#ffffff' : color;
    ctx.lineWidth = STROKE_WIDTHS[strokeWidth];
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  const pushUndo = (ctx) => {
    const canvas = canvasRef.current;
    const snapshot = ctx.getImageData(0, 0, canvas.width, canvas.height);
    setUndoStack(prev => [...prev, snapshot].slice(-MAX_UNDO));
  };

  // ---- Mouse / touch handlers ----

  const handlePointerDown = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);

    setIsDrawing(true);
    startPos.current = pos;
    strokeSnapshot.current = ctx.getImageData(0, 0, canvas.width, canvas.height);

    if (tool === 'pen' || tool === 'eraser') {
      applyCtxStyle(ctx);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    }
  };

  const handlePointerMove = (e) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);

    if (tool === 'pen' || tool === 'eraser') {
      applyCtxStyle(ctx);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    } else if (tool === 'line' || tool === 'rect') {
      ctx.putImageData(strokeSnapshot.current, 0, 0);
      applyCtxStyle(ctx);
      ctx.beginPath();
      if (tool === 'line') {
        ctx.moveTo(startPos.current.x, startPos.current.y);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
      } else {
        ctx.strokeRect(
          startPos.current.x,
          startPos.current.y,
          pos.x - startPos.current.x,
          pos.y - startPos.current.y,
        );
      }
    }
  };

  const handlePointerUp = (e) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    setIsDrawing(false);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    pushUndo(ctx);
  };

  // ---- Toolbar actions ----

  const handleUndo = () => {
    setUndoStack(prev => {
      if (prev.length <= 1) return prev;
      const next = prev.slice(0, -1);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.putImageData(next[next.length - 1], 0, 0);
      return next;
    });
  };

  const handleClear = () => {
    setConfirmClear(true);
  };

  const executeClear = () => {
    setConfirmClear(false);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setUndoStack([ctx.getImageData(0, 0, canvas.width, canvas.height)]);
  };

  // ---- Save ----

  const handleSave = async () => {
    const canvas = canvasRef.current;
    setSaving(true);
    setStatusMsg('Uploading…');
    try {
      // Capture data URL for local preview BEFORE converting to blob
      const previewDataUrl = canvas.toDataURL('image/png');
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      const filename = `drawing_${Date.now()}.png`;
      const file = new File([blob], filename, { type: 'image/png' });
      const { blobPath } = await uploadFileToBlob(file);
      onSave(blobPath, previewDataUrl);
    } catch (err) {
      console.error('Failed to save drawing:', err);
      setStatusMsg('Save failed — please try again.');
      setSaving(false);
    }
  };

  // ==================== Render ====================

  return (
    <Overlay>
      <ModalCard theme={theme}>
        <ModalHeader theme={theme}>
          <ModalTitle theme={theme}><Pencil size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />Drawing Canvas</ModalTitle>
          <CloseBtn theme={theme} onClick={onClose}><XIcon size={18} /></CloseBtn>
        </ModalHeader>

        <ModalBody theme={theme}>
          <Toolbar theme={theme}>
            {/* Tool selector */}
            {TOOLS.map(t => {
              const IconComp = t.icon;
              return (
                <ToolBtn
                  key={t.id}
                  theme={theme}
                  $active={tool === t.id}
                  onClick={() => setTool(t.id)}
                >
                  <IconComp size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />{t.label}
                </ToolBtn>
              );
            })}

            <DividerBar theme={theme} />

            {/* Color palette */}
            {COLORS.map(c => (
              <ColorSwatch
                key={c}
                theme={theme}
                $color={c}
                $active={color === c && tool !== 'eraser'}
                onClick={() => { setColor(c); if (tool === 'eraser') setTool('pen'); }}
                title={c}
              />
            ))}

            <DividerBar theme={theme} />

            {/* Stroke width */}
            {Object.keys(STROKE_WIDTHS).map(w => (
              <ToolBtn
                key={w}
                theme={theme}
                $active={strokeWidth === w}
                onClick={() => setStrokeWidth(w)}
              >
                {w.charAt(0).toUpperCase() + w.slice(1)}
              </ToolBtn>
            ))}

            <DividerBar theme={theme} />

            {/* Undo / Clear */}
            <ToolBtn
              theme={theme}
              onClick={handleUndo}
              disabled={undoStack.length <= 1}
            >
              <Undo2 size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Undo
            </ToolBtn>
            <ToolBtn
              theme={theme}
              onClick={handleClear}
              style={{ color: '#dc2626' }}
            >
              <Trash2 size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Clear
            </ToolBtn>
          </Toolbar>

          <CanvasWrapper ref={wrapperRef} theme={theme}>
            <StyledCanvas
              ref={canvasRef}
              onMouseDown={handlePointerDown}
              onMouseMove={handlePointerMove}
              onMouseUp={handlePointerUp}
              onMouseLeave={handlePointerUp}
              onTouchStart={handlePointerDown}
              onTouchMove={handlePointerMove}
              onTouchEnd={handlePointerUp}
            />
          </CanvasWrapper>
        </ModalBody>

        <ModalFooter theme={theme}>
          <StatusMsg theme={theme}>{statusMsg}</StatusMsg>
          <FooterBtn theme={theme} onClick={onClose}>
            Cancel
          </FooterBtn>
          <FooterBtn theme={theme} $primary onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Drawing'}
          </FooterBtn>
        </ModalFooter>
      </ModalCard>

      <ConfirmDialog
        isOpen={confirmClear}
        title="Clear Canvas"
        message="Clear the entire canvas? This action cannot be undone."
        confirmLabel="Clear"
        cancelLabel="Cancel"
        onConfirm={executeClear}
        onCancel={() => setConfirmClear(false)}
      />
    </Overlay>
  );
};

export default DrawingCanvas;
export { DrawingCanvas };
